import { createFileRoute, redirect, Link, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useArticleCollections, useDeleteArticleCollection } from '@/queries/article-collections';
import { useUserTier } from '@/queries/subscriptions';
import { CollectionCard } from '@/components/CollectionCard';
import { CreateCollectionModal } from '@/components/CreateCollectionModal';
import { CircularLoader } from '@/components/CircularLoader';
import FolderPlusIcon from '@/assets/common/folder-plus.svg?react';
import FolderIcon from '@/assets/common/folder.svg?react';
import ArrowLeftIcon from '@/assets/common/arrow-left.svg?react';
import styles from './collections.module.scss';

export const Route = createFileRoute('/articles/collections/')({
  component: CollectionsPage,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
});

function CollectionsPage() {
  const navigate = useNavigate();
  const userTier = useUserTier();
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Redirect foundation tier users to articles page
  useEffect(() => {
    if (userTier === 'foundation') {
      navigate({
        to: '/articles',
        replace: true,
      });
    }
  }, [userTier, navigate]);

  const { data: collections, isLoading, error } = useArticleCollections();
  const deleteCollection = useDeleteArticleCollection();

  const handleDeleteCollection = (collectionId: string, collectionName: string) => {
    if (confirm(`Are you sure you want to delete "${collectionName}"? This will remove all articles and headings inside it.`)) {
      deleteCollection.mutate(collectionId);
    }
  };

  // Show loading while redirecting foundation users
  if (userTier === 'foundation') {
    return (
      <div className={styles.collections__loading}>
        <CircularLoader />
      </div>
    );
  }

  return (
    <div className={styles.collections}>
      <div className={styles.collections__header}>
        <div className={styles.collections__headerContent}>
          <Link to="/articles" className={styles.collections__backLink}>
            <ArrowLeftIcon />
            Back to Articles
          </Link>
          <h1 className={styles.collections__title}>Collections</h1>
          <p className={styles.collections__subtitle}>
            Organize your articles into collections with nested headings
          </p>
        </div>

        <motion.button
          onClick={() => setShowCreateModal(true)}
          className={styles.collections__createButton}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FolderPlusIcon />
          New Collection
        </motion.button>
      </div>

      {isLoading ? (
        <div className={styles.collections__loading}>
          <CircularLoader />
        </div>
      ) : error ? (
        <div className={styles.collections__error}>
          Error loading collections: {error.message}
        </div>
      ) : collections && collections.length > 0 ? (
        <div className={styles.collections__grid}>
          {collections.map((collection) => (
            <CollectionCard
              key={collection.id}
              collection={collection}
              onDelete={() => handleDeleteCollection(collection.id, collection.name)}
            />
          ))}
        </div>
      ) : (
        <div className={styles.collections__emptyState}>
          <FolderIcon className={styles.collections__emptyIcon} />
          <h3 className={styles.collections__emptyTitle}>No collections yet</h3>
          <p className={styles.collections__emptyText}>
            Create collections to organize your articles into topics, projects, or any structure you like.
          </p>
          <motion.button
            onClick={() => setShowCreateModal(true)}
            className={styles.collections__emptyButton}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FolderPlusIcon />
            Create Your First Collection
          </motion.button>
        </div>
      )}

      <CreateCollectionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </div>
  );
}
